import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT } from '../../config'
import { BaseMiniGameScene, type MiniGameConfig } from './BaseMiniGameScene'

type StirSpeed = 'perfect' | 'good' | 'too_fast' | 'too_slow' | 'idle'

/**
 * Stirring Mini-Game
 * Make circular motions to stir the jam mixture
 */
export class StirringMiniGameScene extends BaseMiniGameScene {
  private pot!: Phaser.GameObjects.Image
  private spoon!: Phaser.GameObjects.Image
  private mixture!: Phaser.GameObjects.Graphics
  private speedMeter!: Phaser.GameObjects.Graphics
  private speedLabel!: Phaser.GameObjects.Text

  private potCenter!: Phaser.Math.Vector2
  private lastAngle: number = 0
  private totalRotation: number = 0
  private rotationHistory: number[] = []
  private currentSpeed: StirSpeed = 'idle'
  private comboCount: number = 0
  private isStirring: boolean = false

  // Tuning constants
  private readonly PERFECT_SPEED_MIN = 2.5
  private readonly PERFECT_SPEED_MAX = 5.0
  private readonly GOOD_SPEED_MIN = 1.5
  private readonly GOOD_SPEED_MAX = 6.5
  private readonly HISTORY_LENGTH = 10

  constructor() {
    super({ key: 'StirringMiniGameScene' })
  }

  getConfig(): MiniGameConfig {
    return {
      maxScore: 2000,
      duration: 30, // 30 seconds
    }
  }

  getNextScene(): string | undefined {
    return 'JarringMiniGameScene' // Next step in recipe
  }

  protected createBackground(): void {
    // Kitchen counter background
    const graphics = this.add.graphics()

    // Warm kitchen colors
    const topColor = Phaser.Display.Color.HexStringToColor('#f5e6d3')
    const bottomColor = Phaser.Display.Color.HexStringToColor('#d4a574')

    for (let y = 0; y < GAME_HEIGHT; y++) {
      const ratio = y / GAME_HEIGHT
      const color = Phaser.Display.Color.Interpolate.ColorWithColor(
        topColor,
        bottomColor,
        100,
        ratio * 100
      )
      graphics.fillStyle(Phaser.Display.Color.GetColor(color.r, color.g, color.b))
      graphics.fillRect(0, y, GAME_WIDTH, 1)
    }
  }

  setupGame(): void {
    this.totalRotation = 0
    this.rotationHistory = []
    this.comboCount = 0
    this.currentSpeed = 'idle'
    this.isStirring = false

    this.potCenter = new Phaser.Math.Vector2(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 20)

    this.createPot()
    this.createSpoon()
    this.createSpeedMeter()
    this.createInstructions()
    this.setupInput()

    // Start the scoring loop
    this.time.addEvent({
      delay: 500,
      callback: () => this.scoreCurrentSpeed(),
      loop: true,
    })
  }

  private createPot(): void {
    // Create pot (top-down view)
    this.pot = this.add.image(this.potCenter.x, this.potCenter.y, 'pot_topdown')
    this.pot.setScale(1.8)

    // Create mixture inside pot
    this.mixture = this.add.graphics()
    this.drawMixture()
  }

  private drawMixture(): void {
    this.mixture.clear()

    // Base jam color
    this.mixture.fillStyle(0xdc143c, 0.9)
    this.mixture.fillCircle(this.potCenter.x, this.potCenter.y, 70)

    // Lighter swirl highlights
    this.mixture.fillStyle(0xff6b6b, 0.6)
    const swirls = 5
    for (let i = 0; i < swirls; i++) {
      const angle = (this.totalRotation * 0.1) + (i * Math.PI * 2 / swirls)
      const dist = 30 + Math.sin(angle * 2) * 15
      const x = this.potCenter.x + Math.cos(angle) * dist
      const y = this.potCenter.y + Math.sin(angle) * dist
      this.mixture.fillCircle(x, y, 12)
    }
  }

  private createSpoon(): void {
    this.spoon = this.add.image(
      this.potCenter.x + 50,
      this.potCenter.y,
      'spoon'
    )
    this.spoon.setScale(0.8)
    this.spoon.setOrigin(0.5, 1)
    this.spoon.setDepth(10)
  }

  private createSpeedMeter(): void {
    // Speed meter background
    const meterX = GAME_WIDTH / 2
    const meterY = GAME_HEIGHT - 80

    // Label
    this.speedLabel = this.add.text(meterX, meterY - 25, 'Speed: Waiting...', {
      fontFamily: 'system-ui',
      fontSize: '18px',
      color: '#5d4037',
    })
    this.speedLabel.setOrigin(0.5)

    // Meter bar
    this.speedMeter = this.add.graphics()
    this.updateSpeedMeter(0)
  }

  private updateSpeedMeter(speed: number): void {
    const meterX = GAME_WIDTH / 2 - 100
    const meterY = GAME_HEIGHT - 70
    const meterWidth = 200
    const meterHeight = 20

    this.speedMeter.clear()

    // Background
    this.speedMeter.fillStyle(0x333333, 0.3)
    this.speedMeter.fillRoundedRect(meterX, meterY, meterWidth, meterHeight, 5)

    // Fill based on speed
    const normalizedSpeed = Math.min(speed / 8, 1)
    const fillWidth = meterWidth * normalizedSpeed

    // Color based on speed zone
    let fillColor: number
    if (speed >= this.PERFECT_SPEED_MIN && speed <= this.PERFECT_SPEED_MAX) {
      fillColor = 0x4caf50 // Green - perfect
    } else if (speed >= this.GOOD_SPEED_MIN && speed <= this.GOOD_SPEED_MAX) {
      fillColor = 0xffc107 // Yellow - good
    } else if (speed > this.GOOD_SPEED_MAX) {
      fillColor = 0xf44336 // Red - too fast
    } else {
      fillColor = 0x2196f3 // Blue - too slow
    }

    this.speedMeter.fillStyle(fillColor, 0.9)
    this.speedMeter.fillRoundedRect(meterX, meterY, fillWidth, meterHeight, 5)

    // Perfect zone indicator
    const perfectStart = meterX + (this.PERFECT_SPEED_MIN / 8) * meterWidth
    const perfectEnd = meterX + (this.PERFECT_SPEED_MAX / 8) * meterWidth
    this.speedMeter.lineStyle(2, 0x4caf50, 0.8)
    this.speedMeter.strokeRect(perfectStart, meterY - 2, perfectEnd - perfectStart, meterHeight + 4)
  }

  private createInstructions(): void {
    const text = this.add.text(
      GAME_WIDTH / 2,
      120,
      '↻ Stir in circles! Keep the speed in the green zone',
      {
        fontFamily: 'system-ui',
        fontSize: '18px',
        color: '#5d4037',
      }
    )
    text.setOrigin(0.5)
    text.setAlpha(0.8)
  }

  private setupInput(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.isComplete) return

      const dist = Phaser.Math.Distance.Between(
        pointer.x, pointer.y,
        this.potCenter.x, this.potCenter.y
      )

      // Only start stirring if clicking near/in the pot
      if (dist < 120) {
        this.isStirring = true
        this.lastAngle = Phaser.Math.Angle.Between(
          this.potCenter.x, this.potCenter.y,
          pointer.x, pointer.y
        )
      }
    })

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.isStirring || this.isComplete) return

      const currentAngle = Phaser.Math.Angle.Between(
        this.potCenter.x, this.potCenter.y,
        pointer.x, pointer.y
      )

      // Calculate angle delta (handle wrap-around)
      let delta = currentAngle - this.lastAngle
      if (delta > Math.PI) delta -= Math.PI * 2
      if (delta < -Math.PI) delta += Math.PI * 2

      this.totalRotation += Math.abs(delta)
      this.rotationHistory.push(Math.abs(delta))

      // Keep history limited
      if (this.rotationHistory.length > this.HISTORY_LENGTH) {
        this.rotationHistory.shift()
      }

      this.lastAngle = currentAngle

      // Update spoon position
      const spoonDist = 50
      this.spoon.x = this.potCenter.x + Math.cos(currentAngle) * spoonDist
      this.spoon.y = this.potCenter.y + Math.sin(currentAngle) * spoonDist
      this.spoon.setRotation(currentAngle + Math.PI / 2)

      // Update mixture animation
      this.drawMixture()

      // Calculate current speed
      this.updateSpeed()
    })

    this.input.on('pointerup', () => {
      this.isStirring = false
    })
  }

  private updateSpeed(): void {
    if (this.rotationHistory.length < 3) {
      this.currentSpeed = 'idle'
      this.speedLabel.setText('Speed: Start stirring!')
      this.updateSpeedMeter(0)
      return
    }

    // Calculate average angular velocity
    const avgDelta = this.rotationHistory.reduce((a, b) => a + b, 0) / this.rotationHistory.length
    const speed = avgDelta * 60 // Convert to approx rotations per second

    // Determine speed category
    if (speed >= this.PERFECT_SPEED_MIN && speed <= this.PERFECT_SPEED_MAX) {
      this.currentSpeed = 'perfect'
      this.speedLabel.setText('Speed: Perfect!')
      this.speedLabel.setColor('#4caf50')
    } else if (speed >= this.GOOD_SPEED_MIN && speed <= this.GOOD_SPEED_MAX) {
      this.currentSpeed = 'good'
      this.speedLabel.setText('Speed: Good')
      this.speedLabel.setColor('#ffc107')
    } else if (speed > this.GOOD_SPEED_MAX) {
      this.currentSpeed = 'too_fast'
      this.speedLabel.setText('Speed: Too fast!')
      this.speedLabel.setColor('#f44336')
      this.createSplashParticle()
    } else if (speed > 0.5) {
      this.currentSpeed = 'too_slow'
      this.speedLabel.setText('Speed: Too slow!')
      this.speedLabel.setColor('#2196f3')
      this.createSmokeParticle()
    } else {
      this.currentSpeed = 'idle'
      this.speedLabel.setText('Speed: Keep stirring!')
      this.speedLabel.setColor('#5d4037')
    }

    this.updateSpeedMeter(speed)
  }

  private scoreCurrentSpeed(): void {
    if (this.isComplete || !this.isStirring) return

    let points = 0
    let feedbackText = ''
    let feedbackColor = 0xffffff

    switch (this.currentSpeed) {
      case 'perfect':
        this.comboCount++
        const multiplier = this.comboCount >= 5 ? 1.5 : 1
        points = Math.floor(50 * multiplier)
        feedbackText = this.comboCount >= 5 ? `Perfect! x${multiplier}` : 'Perfect!'
        feedbackColor = 0x4caf50
        this.createBubbleParticle()
        break
      case 'good':
        this.comboCount = Math.max(0, this.comboCount - 1)
        points = 25
        feedbackText = 'Good'
        feedbackColor = 0xffc107
        break
      case 'too_fast':
        this.comboCount = 0
        points = 10
        feedbackText = 'Slow down!'
        feedbackColor = 0xf44336
        break
      case 'too_slow':
        this.comboCount = 0
        points = 10
        feedbackText = 'Speed up!'
        feedbackColor = 0x2196f3
        break
      default:
        this.comboCount = 0
        return
    }

    this.addScore(points, this.potCenter.x, this.potCenter.y - 80)
    this.showFeedback(feedbackText, this.potCenter.x, this.potCenter.y - 110, feedbackColor)
  }

  private createBubbleParticle(): void {
    const count = 3
    for (let i = 0; i < count; i++) {
      const x = this.potCenter.x + Phaser.Math.Between(-40, 40)
      const y = this.potCenter.y + Phaser.Math.Between(-40, 40)

      const bubble = this.add.circle(x, y, Phaser.Math.Between(4, 8), 0xffffff, 0.7)

      this.tweens.add({
        targets: bubble,
        y: y - 50,
        alpha: 0,
        scale: 1.5,
        duration: 800,
        ease: 'Power2',
        onComplete: () => bubble.destroy(),
      })
    }
  }

  private createSplashParticle(): void {
    if (Math.random() > 0.3) return // Don't spawn every time

    const angle = Math.random() * Math.PI * 2
    const x = this.potCenter.x + Math.cos(angle) * 60
    const y = this.potCenter.y + Math.sin(angle) * 60

    const splash = this.add.circle(x, y, 6, 0xdc143c, 0.8)

    const targetX = x + Math.cos(angle) * 40
    const targetY = y + Math.sin(angle) * 40

    this.tweens.add({
      targets: splash,
      x: targetX,
      y: targetY,
      alpha: 0,
      scale: 0.5,
      duration: 400,
      ease: 'Power2',
      onComplete: () => splash.destroy(),
    })
  }

  private createSmokeParticle(): void {
    if (Math.random() > 0.3) return // Don't spawn every time

    const x = this.potCenter.x + Phaser.Math.Between(-30, 30)
    const y = this.potCenter.y + Phaser.Math.Between(-30, 30)

    const smoke = this.add.circle(x, y, 8, 0x666666, 0.5)

    this.tweens.add({
      targets: smoke,
      y: y - 60,
      alpha: 0,
      scale: 2,
      duration: 1000,
      ease: 'Power1',
      onComplete: () => smoke.destroy(),
    })
  }
}
